import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";

import {
  listProspectStations,
  confirmProspectPublication,
  markProspectLive,
  sendFirstChargeEmail,
  promoteProspect,
  registerProspectStations,
  revealStationKey,
  saveStationCredentials,
  sendConfigEmail,
} from "../services/onboarding/prospects";
import { listVendorConfigs } from "../services/onboarding/vendorConfigs";
import {
  FirstChargeEmailResponse,
  GoLiveResponse,
  ProspectStation,
  SaveStationCredentialsPayload,
  VendorConfig,
} from "../types/onboarding";
import {
  firstChargeEmailErrorMessage,
  goLiveErrorMessage,
  promoteErrorMessage,
  registerStationsErrorMessage,
  revealKeyErrorMessage,
  saveCredentialsErrorMessage,
  sendConfigEmailErrorMessage,
} from "../pages/Onboarding/Prospects/components/stationConfigErrors";
import { GoLiveReport, goLiveReport } from "../utils/ownerPermission";
import { PublishAction } from "../utils/onboardingStates";

interface UseProspectStationsParams {
  id: string;
  toastRef: React.RefObject<Toast>;
  // The prospect's own reload. The station list and the prospect are two
  // separate fetches, and the progression actions change the prospect's stage —
  // so both have to be refreshed for the panel to stay self-consistent.
  reloadProspect: () => Promise<void>;
  // The prospect's current state. Stations appear as the prospect moves on —
  // assigning station IDs creates them — so a stage change on an open page reads
  // the list again.
  stage?: string | null;
}

interface UseProspectStations {
  stations: ProspectStation[];
  allCredentialsSaved: boolean;
  vendorConfigs: VendorConfig[];
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  saveCredentials: (
    stationId: string,
    payload: SaveStationCredentialsPayload
  ) => Promise<boolean>;
  registerStations: () => Promise<boolean>;
  // Reveals one station's access key for manual entry into eRound. Returns null
  // when the reveal fails, so the caller keeps showing the key as hidden.
  revealKey: (stationId: string) => Promise<string | null>;
  promote: () => Promise<boolean>;
  sendEmail: () => Promise<boolean>;
  // Go-live and its resend are the same call; `resend` only changes the wording,
  // since the backend skips the transition when the prospect is already live.
  markLive: (
    stationIds?: string[],
    resend?: boolean,
    action?: PublishAction
  ) => Promise<GoLiveResponse | null>;
  // Re-sends the welcome e-mail for a prospect whose station has already had its
  // first charging session. Changes no state.
  resendFirstChargeEmail: () => Promise<FirstChargeEmailResponse | null>;
}

/**
 * Owns a prospect's station access data plus the manufacturer catalogue the
 * configuration email draws on, and the four ops actions that move a signed
 * prospect to "credentials sent".
 *
 * Deliberately separate from useProspectDetail: the station list is its own
 * fetch (the prospect response carries no credential data), so folding it in
 * would make every station action refetch the prospect, audit log and
 * transitions as well.
 *
 * Every mutation reloads from the server rather than mutating local state, and
 * every failure is reported through the action-specific message maps so no raw
 * backend text reaches the screen.
 */
const useProspectStations = ({
  id,
  toastRef,
  reloadProspect,
  stage,
}: UseProspectStationsParams): UseProspectStations => {
  const [stations, setStations] = useState<ProspectStation[]>([]);
  const [allCredentialsSaved, setAllCredentialsSaved] = useState(false);
  const [vendorConfigs, setVendorConfigs] = useState<VendorConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await listProspectStations(id);
      setStations(data?.stations ?? []);
      setAllCredentialsSaved(data?.all_credentials_saved ?? false);
    } catch (e) {
      console.error("Failed to load prospect stations", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  // The prospect's stage arriving with the page is not a change: the read above
  // already covers it.
  const seenStage = useRef(stage);
  useEffect(() => {
    const before = seenStage.current;
    seenStage.current = stage;
    if (before == null || before === stage) return;
    reload();
  }, [stage, reload]);

  // The catalogue is only needed to tell ops which manufacturer configuration
  // will be used. A failure here must not break the station list, so it is
  // fetched separately and failures are swallowed (the panel then simply omits
  // the line rather than claiming the wrong configuration).
  useEffect(() => {
    let cancelled = false;
    listVendorConfigs()
      .then((configs) => {
        if (!cancelled) setVendorConfigs(configs);
      })
      .catch((e) => console.error("Failed to load vendor configurations", e));
    return () => {
      cancelled = true;
    };
  }, []);

  const showSuccess = useCallback(
    (detail: string) =>
      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail,
        life: 3000,
      }),
    [toastRef]
  );

  const showError = useCallback(
    (summary: string, detail: string) =>
      toastRef.current?.show({
        severity: "error",
        summary,
        detail,
        life: 6000,
      }),
    [toastRef]
  );

  // A composed outcome goes to the toast that matches its severity, so a
  // partial success is never rendered as a plain success.
  const report = useCallback(
    (outcome: GoLiveReport) => {
      if (outcome.severity === "success") {
        showSuccess(outcome.summary);
        return;
      }
      toastRef.current?.show({
        severity: "warn",
        summary: outcome.summary,
        detail: outcome.detail,
        life: 8000,
      });
    },
    [showSuccess, toastRef]
  );

  const saveCredentials = useCallback(
    async (
      stationId: string,
      payload: SaveStationCredentialsPayload
    ): Promise<boolean> => {
      try {
        await saveStationCredentials(id, stationId, payload);
        await reload();
        showSuccess(`Access data saved for ${stationId}.`);
        return true;
      } catch (e) {
        showError("Access data not saved", saveCredentialsErrorMessage(e));
        return false;
      }
    },
    [id, reload, showError, showSuccess]
  );

  const revealKey = useCallback(
    async (stationId: string): Promise<string | null> => {
      try {
        const result = await revealStationKey(id, stationId);
        return result.authorization_key;
      } catch (e) {
        showError("Access key not shown", revealKeyErrorMessage(e));
        return null;
      }
    },
    [id, showError]
  );

  const registerStations = useCallback(async (): Promise<boolean> => {
    try {
      await registerProspectStations(id);
      await Promise.all([reload(), reloadProspect()]);
      showSuccess("Access data confirmed for every station.");
      return true;
    } catch (e) {
      showError("Access data not confirmed", registerStationsErrorMessage(e));
      return false;
    }
  }, [id, reload, reloadProspect, showError, showSuccess]);

  const promote = useCallback(async (): Promise<boolean> => {
    try {
      await promoteProspect(id);
      await Promise.all([reload(), reloadProspect()]);
      // Deliberately says nothing about billing or station permissions: the
      // backend attempts both but does not report whether they succeeded.
      showSuccess("Prospect promoted to customer.");
      return true;
    } catch (e) {
      showError("Prospect not promoted", promoteErrorMessage(e));
      return false;
    }
  }, [id, reload, reloadProspect, showError, showSuccess]);

  const sendEmail = useCallback(async (): Promise<boolean> => {
    try {
      await sendConfigEmail(id);
      await Promise.all([reload(), reloadProspect()]);
      showSuccess("Configuration email sent.");
      return true;
    } catch (e) {
      showError("Configuration email not sent", sendConfigEmailErrorMessage(e));
      // CWA-232: already going out — re-read so the page shows that instead of the stale step.
      if ((e as any)?.response?.status === 409) await reloadProspect();
      return false;
    }
  }, [id, reload, reloadProspect, showError, showSuccess]);

  const markLive = useCallback(
    async (
      stationIds?: string[],
      resend = false,
      // Which endpoint to call. Both do the same thing; picking by stage keeps
      // the audit trail honest about what the agent was doing (research R9).
      action: PublishAction = "go-live"
    ): Promise<GoLiveResponse | null> => {
      try {
        const result =
          action === "confirm-publication"
            ? await confirmProspectPublication(id, stationIds)
            : await markProspectLive(id, stationIds);
        await Promise.all([reload(), reloadProspect()]);

        // Going live has three outcomes that fail independently: the stage
        // change, the e-mail, and granting the customer ownership of their own
        // stations — and the backend reports a failed e-mail or a failed grant
        // with a 200. One composed message so an agent cannot dismiss the first
        // of two toasts and miss the second (CWA-154 finding 7).
        report(goLiveReport({ result, stationCount: stations.length, resend }));
        return result;
      } catch (e) {
        showError(
          resend ? "Email not sent" : "Station not marked as live",
          goLiveErrorMessage(e)
        );
        return null;
      }
    },
    [id, reload, reloadProspect, report, showError, stations.length]
  );

  const resendFirstChargeEmail =
    useCallback(async (): Promise<FirstChargeEmailResponse | null> => {
      try {
        const result = await sendFirstChargeEmail(id);
        // The prospect's stage does not change on this path, but its record does
        // — re-read rather than assume.
        await reloadProspect();

        // A failed send still answers 200. Reporting it as success would tell an
        // agent the customer was written to when they were not.
        if (result?.email_status === "FAILED") {
          showError(
            "Welcome email not sent",
            "The welcome email could not be sent. Please try again."
          );
        } else {
          showSuccess("Welcome email sent again.");
        }
        return result;
      } catch (e) {
        showError("Welcome email not sent", firstChargeEmailErrorMessage(e));
        return null;
      }
    }, [id, reloadProspect, showError, showSuccess]);

  return {
    stations,
    allCredentialsSaved,
    vendorConfigs,
    loading,
    error,
    reload,
    saveCredentials,
    revealKey,
    registerStations,
    promote,
    sendEmail,
    markLive,
    resendFirstChargeEmail,
  };
};

export default useProspectStations;
